import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {MutationObserver | null} */
  #cartDrawerRecommendationsObserver = null;

  /** @type {number} */
  #cartDrawerRecSwiperRaf = 0;

  /** @type {boolean} */
  #swiperRefreshQueued = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#scheduleSwiperRefreshAfterCartMorph);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#initSwipersInDrawer);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    this.#openFromUrlIfRequested();
  }

  #openFromUrlIfRequested = () => {
    let params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch {
      return;
    }

    const keys = ['show_cart_drawer', 'open_cart'];
    const key = keys.find((k) => params.has(k));
    if (!key) return;

    const v = (params.get(key) ?? '').toLowerCase();
    if (v === '0' || v === 'false' || v === 'no') return;

    this.open();
    for (const k of keys) params.delete(k);

    const qs = params.toString();
    const { pathname, hash } = window.location;
    window.history.replaceState(window.history.state, '', `${pathname}${qs ? `?${qs}` : ''}${hash}`);
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#scheduleSwiperRefreshAfterCartMorph);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#initSwipersInDrawer);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    this.#teardownRecommendationsObserver();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      await this.closeDialog();
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * `cart:update` is dispatched before `morphSection` runs; refresh after the current task so DOM matches the new cart.
   */
  #scheduleSwiperRefreshAfterCartMorph = () => {
    if (!this.refs.dialog?.open) return;
    if (this.#swiperRefreshQueued) return;
    this.#swiperRefreshQueued = true;
    queueMicrotask(() => {
      this.#swiperRefreshQueued = false;
      if (!this.refs.dialog?.open) return;
      this.#refreshCartDrawerSwipers();
    });
  };

  #teardownRecommendationsObserver = () => {
    this.#cartDrawerRecommendationsObserver?.disconnect();
    this.#cartDrawerRecommendationsObserver = null;
    const recRoot = this.querySelector('#product-recommendations-cart-drawer');
    if (recRoot instanceof HTMLElement) {
      delete recRoot.dataset.cartDrawerRecObserver;
    }
  };

  /**
   * Destroy swiper instance(s) in the recommendations strip, then re-init (needed after section morph).
   */
  #refreshCartDrawerSwipers = () => {
    const themeSwiper = /** @type {Window & { ThemeSwiper?: { init: (root?: ParentNode) => void; destroy?: (root?: ParentNode) => void } }} */ (
      window
    ).ThemeSwiper;
    if (typeof themeSwiper?.init !== 'function') return;

    const recHost = this.querySelector('[data-cart-drawer-recommendations]');
    if (recHost instanceof HTMLElement && typeof themeSwiper.destroy === 'function') {
      themeSwiper.destroy(recHost);
    }

    this.#teardownRecommendationsObserver();

    themeSwiper.init(this);

    const recRoot = this.querySelector('#product-recommendations-cart-drawer');
    if (!(recRoot instanceof HTMLElement)) return;

    const refreshSwipers = () => {
      cancelAnimationFrame(this.#cartDrawerRecSwiperRaf);
      this.#cartDrawerRecSwiperRaf = requestAnimationFrame(() => {
        themeSwiper.init(this);
        requestAnimationFrame(() => {
          themeSwiper.init(this);
          const swiperEl = this.querySelector('.cart-drawer-recommendations__swiper[data-swiper]');
          const tw = /** @type {Window & { ThemeSwiper?: { getInstance?: (el: HTMLElement) => { update?: () => void } | undefined } }} */ (
            window
          );
          if (swiperEl instanceof HTMLElement) {
            tw.ThemeSwiper?.getInstance?.(swiperEl)?.update?.();
          }
        });
      });
    };

    if (recRoot.querySelector('product-recommendations')) {
      if (recRoot.dataset.cartDrawerRecObserver === 'true') return;
      recRoot.dataset.cartDrawerRecObserver = 'true';
      this.#cartDrawerRecommendationsObserver = new MutationObserver(refreshSwipers);
      this.#cartDrawerRecommendationsObserver.observe(recRoot, { childList: true, subtree: true });
    } else {
      refreshSwipers();
    }
  };

  #initSwipersInDrawer = () => {
    this.#refreshCartDrawerSwipers();
  };

  #updateStickyState() {
    const { dialog } = this.refs;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  open() {
    this.showDialog();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
